/**
 * Vertex AI Connections List Component
 *
 * Displays a table of Vertex AI connections with sync and delete actions.
 */

import { useState } from "react";
import { Trash2, RefreshCw, Clock, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useVertexConnections,
  useDeleteVertexConnection,
  useSyncVertexConnection,
} from "@/hooks/useVertex";
import type { VertexConnectionRead } from "@/types/vertex";

export function VertexConnectionsList() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<VertexConnectionRead | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: connectionsData, isLoading } = useVertexConnections(1, 100);
  const deleteMutation = useDeleteVertexConnection();
  const syncMutation = useSyncVertexConnection();

  const connections = connectionsData?.data ?? [];

  const isBusy = (id: number) => syncingId === id || deletingId === id;

  const handleSyncClick = async (connection: VertexConnectionRead) => {
    if (isBusy(connection.id)) return;
    setActionError(null);
    setSyncingId(connection.id);
    try {
      await syncMutation.mutateAsync(connection.id);
    } catch (error) {
      setActionError(
        `Failed to sync "${connection.connection_name}": ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setSyncingId(null);
    }
  };

  const handleDeleteClick = (connection: VertexConnectionRead) => {
    if (isBusy(connection.id)) return;
    setConnectionToDelete(connection);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!connectionToDelete || deletingId) return;
    setActionError(null);
    setDeletingId(connectionToDelete.id);
    try {
      await deleteMutation.mutateAsync(connectionToDelete.id);
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    } catch (error) {
      setActionError(
        `Failed to delete "${connectionToDelete.connection_name}": ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (connection: VertexConnectionRead) => {
    if (!connection.is_active) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Inactive
        </Badge>
      );
    }
    if (connection.consecutive_errors > 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-700">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-1">No Connections Configured</h3>
        <p className="text-sm text-muted-foreground">
          Add your first Gemini Agent Platform connection to start ingesting GenAI traces
        </p>
      </div>
    );
  }

  return (
    <>
      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200 mb-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Connection</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Last Sync</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {connections.map((connection) => (
            <TableRow key={connection.id}>
              <TableCell className="font-medium">
                <div>
                  <div>{connection.connection_name}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-xs">
                    {connection.bigquery_dataset}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  {getStatusBadge(connection)}
                  {connection.last_error && (
                    <div className="text-xs text-destructive line-clamp-1">
                      {connection.last_error}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm font-mono">{connection.gcp_project_id}</span>
              </TableCell>
              <TableCell>
                <span className="text-sm font-mono">{connection.gcp_region}</span>
              </TableCell>
              <TableCell>
                <div className="text-sm">{formatDate(connection.last_sync_at)}</div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSyncClick(connection)}
                    disabled={isBusy(connection.id)}
                    title={`Sync ${connection.connection_name} now`}
                    aria-label={`Sync ${connection.connection_name} now`}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${syncingId === connection.id ? "animate-spin" : ""}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(connection)}
                    disabled={isBusy(connection.id)}
                    className="text-destructive hover:text-destructive"
                    title={`Delete ${connection.connection_name}`}
                    aria-label={`Delete ${connection.connection_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && deletingId) return;
          setDeleteDialogOpen(open);
          if (!open) setConnectionToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Connection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{connectionToDelete?.connection_name}"? Ingestion
              will stop immediately. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={!!deletingId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={!!deletingId}
            >
              {deletingId ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
